import React, { useCallback } from 'react';
import {useLingui} from "@lingui/react";
import {connect} from "react-redux";
import LockAccountingPeriod, {LockAccountingPeriodInfo} from "./LockAccountingPeriod";
import CustomerCreditLimit, {CustomerCreditLimitInfo} from "./CustomerCreditLimit";
import OutOfStock, {OutOfStockInfo} from "./OutOfStock";
import PasswordCheck, { PasswordCheckInfo } from './PasswordCheck';
import BillDuplicateReference, { BillDuplicateReferenceInfo } from './BillDuplicateReference';
import InstructionRunToday, {InstructionRunTodayInfo} from './InstructionRunToday';
import AppActions from "bukku/redux/App/actions";
import {store} from "bukku/redux/store";
import {usePasscode} from "./helpers";
import {Modal} from "antd";
import AssetRepost, {AssetRepostInfo} from "./AssetRepost";
import GenericWarningModal, { GenericWarningModalInfo } from './GenericWarningModal';
import CustomExchangeRate, {CustomExchangeRateInfo} from './CustomExchangeRate';
import NumberUsed, { NumberUsedInfo } from './NumberUsed';
import Hotkeys from 'react-hot-keys';

export const InterceptorType = {
    lock_accounting_period: 'PERIOD_LOCK',
    credit_limit: 'CREDIT_LIMIT',
    out_of_stock: 'INVENTORY_CHECK',
    password_check: 'PASSWORD_CHECK',
    asset_repost: 'ASSET_REPOST',
    bill_duplicate_reference: 'BILL_DUPLICATE_REFERENCE_NO',
    account_used: 'INTERCEPTOR_ACCOUNT_USED',
    custom_exchange_rate: 'CUSTOM_EXCHANGE_RATE_UPDATED',
    number_used: 'NUMBER_USED',
    instruction_run_today: 'INSTRUCTION_RUN_TODAY',
};

const { closeInterceptor } = AppActions;

function Empty() {
    return <div />;
}

function Interceptors(props) {
    const { isVisible, type, numTries, action, closeInterceptor, message, items, settings, details } = props;

    const [confirmLoading, setConfirmLoading] = React.useState(false);
    const [passcode, setPasscode, passcodeRef] = usePasscode(isVisible, confirmLoading);

    // componentDidUpdate for numTries
    React.useEffect(() => {
        setConfirmLoading(false);
    }, [numTries]);

    // componentDidUpdate for isVisible
    React.useEffect(() => {
        setConfirmLoading(false);
    }, [isVisible]);

    function handleOk(newAction) {
        setConfirmLoading(true);
        store.dispatch(newAction);
    }

    const handleCancel = useCallback(() => {
        setConfirmLoading(false);
        closeInterceptor();
    }, [closeInterceptor]);

    function getComponent() {
        switch (type) {
            case InterceptorType.lock_accounting_period:
                return [LockAccountingPeriod, LockAccountingPeriodInfo];
            case InterceptorType.credit_limit:
                return [CustomerCreditLimit, CustomerCreditLimitInfo];
            case InterceptorType.out_of_stock:
                return [OutOfStock, OutOfStockInfo];
            case InterceptorType.password_check:
                return [PasswordCheck, PasswordCheckInfo];
            case InterceptorType.asset_repost:
                return [AssetRepost, AssetRepostInfo];
            case InterceptorType.bill_duplicate_reference:
                return [BillDuplicateReference, BillDuplicateReferenceInfo];
            // generic interceptor - message from backend
            case InterceptorType.account_used:
                return [GenericWarningModal, GenericWarningModalInfo];
            case InterceptorType.custom_exchange_rate:
                return [CustomExchangeRate, CustomExchangeRateInfo];
            case InterceptorType.number_used:
                return [NumberUsed, NumberUsedInfo];
            case InterceptorType.instruction_run_today:
                return [InstructionRunToday, InstructionRunTodayInfo];
            default:
                return [Empty, {title: () => ""}];
        }
    }

    useLingui();
    const [Component, Info] = getComponent();
    const componentProps = {
        message,
        items,
        settings,
        handleOk,
        passcode,
        setPasscode,
        passcodeRef,
        action,
        details,
    };

    const isOkDisabled = (!passcode && !Info?.noPasscode) || Info?.okButtonProps?.disabled;
    return (

        <Modal
            title={Info.title()}
            open={isVisible}
            destroyOnClose
            okText={Info.okText || 'OK'}
            onOk={() => Info.onOk(passcode, handleOk, action, closeInterceptor)}
            okButtonProps={{
                disabled: isOkDisabled,
                ...(Info.okButtonProps || {}),
            }}
            onCancel={Info.onCancel ? () => Info.onCancel(closeInterceptor) : handleCancel}
            confirmLoading={confirmLoading}
            bodyStyle={Info.bodyStyle}
            width={Info.width ?? 520}
            afterOpenChange={open => {
                if (open && passcodeRef.current)
                    passcodeRef.current?.focus();
            }}
        >
            <Hotkeys
                keyName="enter"
                onKeyDown={keyName => {
                    switch (keyName) {
                        case "enter":
                            Info.onOk(passcode, handleOk, action);
                            return null;
                        default:
                            return null;
                    }
                }}
                filter={() => true}
            >
                <Component {...componentProps} />
            </Hotkeys>
        </Modal>
    );
}

export default connect(state => ({
    isVisible: state.App.interceptor.isVisible,
    type: state.App.interceptor.type,
    message: state.App.interceptor.message,
    items: state.App.interceptor.items,
    settings: state.App.interceptor.settings,
    action: state.App.interceptor.action,
    numTries: state.App.interceptor.numTries,
    details: state.App.interceptor.details,
}), {
    closeInterceptor,
})(Interceptors);
